import type {
  CashRestrictionPurpose,
  DistributionPaymentStatus,
  DistributionStatus,
  PolicyVersionStatus,
  ReserveComponentKind,
  ReserveHybridRule,
  ReservePolicyType,
  WarningSeverity,
} from '@mfos/shared';
import type { BadgeTone } from '../../components/Badge';

interface Display {
  readonly label: string;
  readonly tone: BadgeTone;
}

export const DISTRIBUTION_STATUS: Readonly<Record<DistributionStatus, Display>> = {
  PROPOSED: { label: 'Proposed — not binding', tone: 'info' },
  APPROVED: { label: 'Declared — awaiting payment', tone: 'warn' },
  SCHEDULED: { label: 'Scheduled', tone: 'warn' },
  PARTIALLY_PAID: { label: 'Partly paid', tone: 'warn' },
  PAID: { label: 'Paid', tone: 'ok' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  REVERSED: { label: 'Reversed', tone: 'neutral' },
};

export const ALLOCATION_PAYMENT_STATUS: Readonly<Record<DistributionPaymentStatus, Display>> = {
  PENDING: { label: 'Not paid yet', tone: 'warn' },
  SCHEDULED: { label: 'Scheduled', tone: 'warn' },
  PARTIALLY_PAID: { label: 'Partly paid', tone: 'warn' },
  PAID: { label: 'Paid', tone: 'ok' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const POLICY_STATUS: Readonly<Record<PolicyVersionStatus, Display>> = {
  PROPOSED: { label: 'Proposed — awaiting approval', tone: 'info' },
  APPROVED: { label: 'Approved', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'neutral' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
};

export const SEVERITY: Readonly<Record<WarningSeverity, Display>> = {
  BLOCKING: { label: 'Blocks distribution', tone: 'down' },
  WARNING: { label: 'Warning', tone: 'warn' },
  INFO: { label: 'Note', tone: 'info' },
};

export const POLICY_TYPE_LABELS: Readonly<Record<ReservePolicyType, string>> = {
  FIXED_AMOUNT: 'Fixed minimum amount',
  MONTHS_OF_EXPENSES: 'Months of average expenses',
  PERCENTAGE_OF_CASH: 'Percentage of operating cash',
  HYBRID: 'Combination of the above',
};

export const POLICY_TYPE_OPTIONS = (Object.keys(POLICY_TYPE_LABELS) as ReservePolicyType[]).map(
  (value) => ({ value, label: POLICY_TYPE_LABELS[value] }),
);

export const HYBRID_RULE_LABELS: Readonly<Record<ReserveHybridRule, string>> = {
  GREATER_OF: 'Keep the greatest of the components',
  SUM_OF: 'Keep the sum of the components',
};

export const HYBRID_RULE_OPTIONS = (Object.keys(HYBRID_RULE_LABELS) as ReserveHybridRule[]).map(
  (value) => ({ value, label: HYBRID_RULE_LABELS[value] }),
);

export const COMPONENT_LABELS: Readonly<Record<ReserveComponentKind, string>> = {
  FIXED_AMOUNT: 'Fixed minimum',
  MONTHS_OF_EXPENSES: 'Months of expenses',
  PERCENTAGE_OF_CASH: 'Share of operating cash',
};

export const PURPOSE_LABELS: Readonly<Record<CashRestrictionPurpose, string>> = {
  TAX: 'Taxes',
  PAYROLL: 'Payroll',
  CONTRACTUAL_LIABILITY: 'Contractual liability',
  APPROVED_BUDGET: 'Approved budget',
  OTHER: 'Other',
};

export const PURPOSE_OPTIONS = (Object.keys(PURPOSE_LABELS) as CashRestrictionPurpose[]).map(
  (value) => ({ value, label: PURPOSE_LABELS[value] }),
);

export const distributionsBase = (workspaceId: string) => `/w/${workspaceId}/finance/distributions`;

export function distributionRef(number: number): string {
  return `DIST-${String(number)}`;
}

/** Statuses where money has been promised to founders and may still be paid. */
export function isDeclared(status: DistributionStatus): boolean {
  return status === 'APPROVED' || status === 'SCHEDULED' || status === 'PARTIALLY_PAID';
}
